import React, { useState, useRef } from "react";
import PropTypes from "prop-types";

import textFieldStyles from "../TextField/TextField.module.scss";
import styles from "./FileUpload.module.scss";

import Button from "../Button/Button";
import Label from "../Label/Label";
import { LoadingSpinner } from "../Loading/Loading";

import clsx from "clsx";
import { CloseIcon } from "../../icons/CloseIcon";
import { UploadIcon } from "../../icons/UploadIcon";
import { UploadPrimaryIcon } from "../../icons/UploadPrimaryIcon";

const FileUpload = ({
  defaultValue,
  displayName,
  errors = {},
  disabled,
  label,
  labelColor,
  labelType,
  name,
  required,
  upperHelpText,
  lowerHelpText,
  onChange,
  placeholder,
  multiple,
  maxFileSize,
  fileExtensions,
  readOnly,
  mode = FileUpload.modes.FIELD,
  dataTestId,
}) => {
  const inputFileRef = useRef(null);
  const [processing, setProcessing] = useState(false);
  const [filenames, setFilenames] = useState(
    defaultValue.map((url, idx) => ({
      name: getDisplayName({ url, displayName, multiple, idx }),
      url,
    }))
  );
  const [customErrors, setCustomErrors] = useState();
  const hasError = errors[name] || customErrors;

  const onFileChange = async (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFiles = event.target.files;
      const validationError = validate({ files: selectedFiles, maxFileSize, fileExtensions });
      if (validationError) return setCustomErrors(validationError);

      setProcessing(true);
      if (typeof onChange === "function") {
        const uploadedFileUrls = await onChange(event.target.files);
        if (uploadedFileUrls) {
          const uploadedFilenames = uploadedFileUrls.map((url, idx) => ({
            name: getDisplayName({ url, displayName, multiple, idx }),
            url,
          }));
          setFilenames(uploadedFilenames);
        }
      } else {
        const selectedFilenames = Array.from(selectedFiles).map((f) => ({ name: f.name }));
        setFilenames(selectedFilenames);
      }
      setProcessing(false);
    }
  };

  const onClick = (e) => {
    if (e.target.tagName.toLowerCase() === "a") {
      // exclude click events from anchor tags
      return e.stopPropagation();
    }
    if (disabled || readOnly) return;
    setCustomErrors();
    inputFileRef.current.click();
  };

  const onClear = async (e) => {
    e.stopPropagation();
    if (disabled || readOnly) return;

    inputFileRef.current.value = "";
    setCustomErrors();
    if (typeof onChange === "function") {
      await onChange([]);
    }
    setFilenames([]);
  };

  return (
    <div className={`${styles.FileUpload} ${textFieldStyles.textField}`} data-testid={dataTestId}>
      <Label color={labelColor} htmlFor={name} required={required} type={labelType}>
        {label}
      </Label>
      {upperHelpText ? (
        <span className={`${textFieldStyles.helpText} mb-1`} data-testid="UpperHelpText">
          {upperHelpText}
        </span>
      ) : null}
      <div
        className={`input-group ${styles.InputGroup} ${
          mode === FileUpload.modes.BUTTON
            ? "d-none"
            : disabled
            ? textFieldStyles.inputGroupDisabled
            : textFieldStyles.inputGroup
        } ${hasError ? textFieldStyles.invalid : ""}`}
        onClick={onClick}
      >
        <div className={`input-group-prepend ${textFieldStyles.inputGroupPrepend}`}>
          <span className={`input-group-text ${textFieldStyles.inputGroupText}`}>
            {processing ? (
              <LoadingSpinner size={"Small"} />
            ) : (
              <UploadIcon className={clsx(textFieldStyles.image, styles.UploadIcon)} />
            )}
          </span>
        </div>
        <div className={`${styles.FileUploadInput} ${textFieldStyles.inputGroupText}`}>
          <input
            type="file"
            multiple={multiple}
            onChange={onFileChange}
            accept={fileExtensions.join(",")}
            ref={inputFileRef}
            data-testid="FileInput"
          />
          <SelectedFiles values={filenames} processing={processing} placeholder={placeholder} />
        </div>
        {filenames.length > 0 ? (
          <div
            className={`input-group-append ${textFieldStyles.inputGroupAppend}`}
            onClick={onClear}
            data-testid="FileInputClear"
          >
            <span className={`input-group-text ${textFieldStyles.inputGroupText}`}>
              <CloseIcon className={`${textFieldStyles.image} ${styles.CloseIcon}`} />
            </span>
          </div>
        ) : null}
      </div>

      {mode === FileUpload.modes.BUTTON ? (
        <div>
          {filenames.map((f, i) => (
            <div key={i}>
              <a href={f.url}>{f.name}</a>
            </div>
          ))}
          <div className="mt-4" />
          <Button
            icon={
              <div className={styles.UploadPrimaryIcon}>
                <UploadPrimaryIcon />
              </div>
            }
            label="Upload"
            onClick={onClick}
            isLoading={processing}
            loadingText="Uploading..."
            type="PrimaryInverse"
          />
        </div>
      ) : null}

      <div className={`d-none form-control ${hasError ? "is-invalid" : ""}`} />
      <div className={`${hasError ? `${textFieldStyles.errorMessage} invalid-feedback mt-2` : ""}`}>
        {errors[name] && errors[name].type === "required" && <span>This field is required.</span>}
        {customErrors && <span>{customErrors}</span>}
      </div>
      {lowerHelpText ? (
        <span className={`${textFieldStyles.helpText} mt-1`} data-testid="LowerHelpText">
          {lowerHelpText}
        </span>
      ) : null}
    </div>
  );
};

const SelectedFiles = ({ values, processing, placeholder }) => {
  if (!values || values.length === 0)
    return <div className={styles.Placeholder}>{processing ? "Uploading..." : placeholder}</div>;

  return (
    <div className={styles.Filenames}>
      {values.map(({ name, url }, idx) => (
        <span key={idx} className={styles.Filename}>
          {url ? (
            <a href={url} rel="noreferrer" target="_blank">
              {name}
            </a>
          ) : (
            name
          )}
        </span>
      ))}
    </div>
  );
};

export const validate = ({ files, maxFileSize, fileExtensions }) => {
  for (const f of Array.from(files)) {
    if (f.size > maxFileSize) return `File exceeds max file size of ${bytesToSize(maxFileSize)}.`;

    const fileExtError = `Invalid file type. Allowed file types: ${fileExtensions.join(", ")}`;
    try {
      const ext = getFileExtension(f.type);
      if (!fileExtensions.includes(`.${ext}`)) return fileExtError;
    } catch (e) {
      return fileExtError;
    }
  }
};

export const getDisplayName = ({ url, displayName, multiple, idx }) => {
  const filename = url.substring(url.lastIndexOf("/") + 1);
  const extension = filename.substr(filename.lastIndexOf("."));

  if (displayName) return `${displayName}${multiple ? idx + 1 : ""}${extension}`;
  return filename.length > 20 ? `${filename.substring(0, 10)}${extension}` : filename;
};

export const bytesToSize = (bytes) => {
  var sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes === 0) return "0 Byte";
  var i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
  return Math.round(bytes / Math.pow(1024, i), 2) + " " + sizes[i];
};

export const getFileExtension = (type) => {
  switch (type) {
    case "application/pdf":
      return "pdf";
    case "application/msword":
      return "doc";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "docx";
    case "application/vnd.oasis.opendocument.text":
      return "odt";
    default:
      // Don't allow user to upload other types of file
      throw Error("Unsupported file type");
  }
};

FileUpload.propTypes = {
  defaultValue: PropTypes.array,
  displayName: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    type: PropTypes.string,
    message: PropTypes.string,
  }),
  label: PropTypes.string,
  labelColor: PropTypes.string,
  name: PropTypes.string,
  placeholder: PropTypes.string,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  upperHelpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  lowerHelpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  fileExtensions: PropTypes.array,
  maxFileSize: PropTypes.number,
  multiple: PropTypes.bool,
  dataTestId: PropTypes.string,
};

FileUpload.defaultProps = {
  defaultValue: [],
  displayName: "",
  disabled: false,
  errors: {},
  label: "",
  labelColor: "",
  name: "",
  placeholder: "Choose file to upload...",
  readOnly: false,
  required: false,
  upperHelpText: "",
  lowerHelpText: "",
  multiple: false,
  maxFileSize: 5 * 1024 * 1024, // 5 MB
  fileExtensions: [".doc", ".docx", ".pdf"],
  dataTestId: "FileUpload",
};

FileUpload.modes = {
  BUTTON: "button",
  FIELD: "field",
};

export default FileUpload;
